import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Users, BarChart2, DollarSign, Activity, TrendingUp, Shield } from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from "recharts";
import axios from "axios";

const API = `${process.env.REACT_APP_API_URL}/api`;
const PIE_COLORS = ["#6b7280", "#10b981", "#3b82f6", "#a855f7"];
const PLAN_COLORS = { free: "#6b7280", starter: "#10b981", pro: "#3b82f6", business: "#a855f7" };

const container = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.07 } } };
const item = { hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0 } };

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="rounded-md border border-border bg-card p-5">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-sm flex items-center justify-center bg-secondary/60">
          <Icon className={`w-4 h-4 ${color || "text-brand-primary"}`} />
        </div>
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
          <div className="font-heading text-2xl font-bold mt-0.5" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>{value}</div>
        </div>
      </div>
    </div>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-sm border border-border bg-card px-3 py-2 shadow-lg">
      <p className="text-xs font-mono text-muted-foreground">{label}</p>
      <p className="text-sm font-semibold">{payload[0].value} analisis</p>
    </div>
  );
}

const formatIDR = (v) => new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(v);
const fmtDate = (d) => { const p = d.split("-"); return `${p[2]}/${p[1]}`; };

export default function AdminDashboard() {
  const [stats, setStats]       = useState(null);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/stats`, { withCredentials: true }).then(r => setStats(r.data)).catch(() => {});
    axios.get(`${API}/admin/analytics`, { withCredentials: true }).then(r => setAnalytics(r.data)).catch(() => {});
  }, []);

  const pieData = analytics ? Object.entries(analytics.plan_distribution).map(([name, value]) => ({ name, value })) : [];

  return (
    <div className="p-6 md:p-8" data-testid="admin-dashboard">
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-bold">Dashboard</h1>
        <p className="text-sm text-muted-foreground mt-1">Ringkasan platform Veriksa.</p>
      </div>

      {stats && (
        <motion.div variants={container} initial="hidden" animate="show" className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
          {[
            { icon: Users,    label: "Total Users",  value: stats.total_users,    color: "text-foreground" },
            { icon: Activity, label: "Free",         value: stats.free_users,     color: "text-muted-foreground" },
            { icon: Activity, label: "Starter",      value: stats.starter_users,  color: "text-emerald-400" },
            { icon: BarChart2,label: "Pro",          value: stats.pro_users,      color: "text-brand-primary" },
            { icon: Shield,   label: "Business",     value: stats.business_users, color: "text-purple-400" },
            { icon: Activity, label: "Analyses",     value: stats.total_analyses, color: "text-foreground" },
          ].map(s => <motion.div key={s.label} variants={item}><StatCard {...s} /></motion.div>)}
        </motion.div>
      )}

      {analytics && (
        <>
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="rounded-md border border-verdict-real/20 bg-verdict-real/5 p-5 mb-8 flex items-center justify-between">
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1">Total Revenue</div>
              <div className="font-heading text-3xl font-bold text-verdict-real">{formatIDR(analytics.total_revenue)}</div>
            </div>
            <DollarSign className="w-10 h-10 text-verdict-real/20" />
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
              className="lg:col-span-2 rounded-md border border-border bg-card p-5" data-testid="daily-chart">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-4 h-4 text-brand-primary" />
                <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Analisis Harian — 30 Hari</span>
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={analytics.daily_chart} margin={{ top: 4, right: 8, bottom: 4, left: -20 }}>
                  <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: 10, fill: "#6b7280", fontFamily: "monospace" }} interval={4} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: "#6b7280", fontFamily: "monospace" }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Line type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
              className="rounded-md border border-border bg-card p-5" data-testid="plan-pie-chart">
              <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-4">Distribusi Plan</div>
              <ResponsiveContainer width="100%" height={170}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={72} paddingAngle={3} dataKey="value">
                    {pieData.map((entry, i) => <Cell key={entry.name} fill={PIE_COLORS[i % 4]} />)}
                  </Pie>
                  <Legend formatter={v => <span style={{ fontSize: 11, fontFamily: "monospace", textTransform: "capitalize" }}>{v}</span>} />
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </motion.div>
          </div>

          {analytics.top_users?.length > 0 && (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
              className="rounded-md border border-border bg-card overflow-hidden">
              <div className="px-6 py-4 border-b border-border text-xs font-mono uppercase tracking-widest text-muted-foreground">Top 10 Active Users</div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr className="border-b border-border">{["User","Plan","Analyses","Joined"].map(h => <th key={h} className="px-4 py-3 text-left text-xs font-mono uppercase tracking-widest text-muted-foreground">{h}</th>)}</tr></thead>
                  <tbody>
                    {analytics.top_users.map(u => (
                      <tr key={u.user_id} className="border-b border-border last:border-0 hover:bg-secondary/20 transition-colors">
                        <td className="px-4 py-3"><div className="text-sm font-medium truncate max-w-[180px]">{u.name}</div><div className="text-xs font-mono text-muted-foreground truncate max-w-[180px]">{u.email}</div></td>
                        <td className="px-4 py-3 text-xs font-mono capitalize" style={{ color: PLAN_COLORS[u.plan || "free"] }}>{u.plan || "free"}</td>
                        <td className="px-4 py-3 text-sm font-mono font-semibold">{u.usage_count || 0}</td>
                        <td className="px-4 py-3 text-xs font-mono text-muted-foreground">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          )}
        </>
      )}
    </div>
  );
}
